import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { motion, AnimatePresence } from "framer-motion";
import { Bell, CheckCircle2, Wifi, WifiOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { playNotificationSound, fireBrowserNotification, ensureNotificationPermission } from "@/lib/notify";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function NotificationsBell() {
  const { user } = useAuth();
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const wsRef = useRef(null);
  const soundRef = useRef(user?.notification_sound || "chime");

  useEffect(() => {
    soundRef.current = user?.notification_sound || "chime";
  }, [user?.notification_sound]);

  // Request browser notification permission (idempotent) once we have a user
  useEffect(() => {
    if (user) ensureNotificationPermission();
  }, [user]);

  const load = async () => {
    try {
      const [{ data: c }, { data: list }] = await Promise.all([
        api.get("/notifications/count"),
        api.get("/notifications", { params: { limit: 15 } }),
      ]);
      setUnread(c.unread || 0);
      setItems(list);
    } catch { /* ignore */ }
  };

  useEffect(() => { load(); }, []);

  // WebSocket subscription for real-time push
  useEffect(() => {
    const token = localStorage.getItem("crm_token");
    if (!token || !BACKEND_URL) return;
    let cancelled = false;
    let retryTimer = null;

    const connect = () => {
      if (cancelled) return;
      const wsUrl = BACKEND_URL.replace(/^http/, "ws") + `/api/ws/notifications?token=${encodeURIComponent(token)}`;
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => setWsConnected(true);
      ws.onclose = () => {
        setWsConnected(false);
        if (!cancelled) retryTimer = setTimeout(connect, 5000);
      };
      ws.onerror = () => { /* onclose will fire */ };
      ws.onmessage = (ev) => {
        try {
          const payload = JSON.parse(ev.data);
          if (payload.type === "notification" && payload.data) {
            const n = payload.data;
            setItems((prev) => [n, ...prev].slice(0, 15));
            setUnread((u) => u + 1);
            // In-app toast (visible while tab focused)
            toast(n.title, { description: n.body });
            // Sound
            playNotificationSound(soundRef.current);
            // Native OS notification (only when tab hidden — see helper)
            fireBrowserNotification({ title: n.title, body: n.body, kind: n.kind, url: n.url });
          }
        } catch { /* ignore */ }
      };
    };
    connect();
    return () => { cancelled = true; if (retryTimer) clearTimeout(retryTimer); try { wsRef.current?.close(); } catch { /* ignore */ } };
  }, []);

  const markAll = async () => {
    await api.post("/notifications/mark-read", { all: true });
    load();
  };

  const iconColor = { critical: "text-crimson", warning: "text-yellow-600", ai: "text-emerald-brand", info: "text-muted-foreground" };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="relative h-9 w-9 rounded-lg border border-border bg-card hover:bg-muted transition-colors grid place-items-center" data-testid="notif-bell" title={wsConnected ? "Live" : "Reconnecting…"}>
          <Bell className="h-4 w-4" />
          {wsConnected ? (
            <Wifi className="absolute bottom-0.5 right-0.5 h-2 w-2 text-emerald-brand" />
          ) : (
            <WifiOff className="absolute bottom-0.5 right-0.5 h-2 w-2 text-muted-foreground" />
          )}
          {unread > 0 && (
            <motion.span
              initial={{ scale: 0 }} animate={{ scale: 1 }}
              className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-crimson text-white text-[10px] font-mono rounded-full grid place-items-center"
            >
              {unread > 99 ? "99+" : unread}
            </motion.span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0 rounded-2xl overflow-hidden" data-testid="notif-panel">
        <div className="flex items-center justify-between p-3 border-b border-border">
          <div className="flex items-center gap-2">
            <div className="font-display font-semibold">Notifications</div>
            <div className={`inline-flex items-center gap-1 text-[10px] font-mono uppercase px-2 py-0.5 rounded-full ${wsConnected ? "bg-emerald-brand/12 text-emerald-brand" : "bg-muted text-muted-foreground"}`}>
              <span className={`h-1.5 w-1.5 rounded-full ${wsConnected ? "bg-emerald-brand" : "bg-muted-foreground"}`} />
              {wsConnected ? "Live" : "Offline"}
            </div>
          </div>
          {unread > 0 && (
            <Button size="sm" variant="ghost" className="rounded-lg text-xs" onClick={markAll} data-testid="notif-mark-all"><CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Mark all read</Button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto">
          <AnimatePresence>
            {items.length === 0 && (
              <div className="p-8 text-center text-sm text-muted-foreground">You&apos;re all caught up.</div>
            )}
            {items.map((n) => (
              <motion.a
                key={n.id}
                href={n.url || "#"}
                initial={{ opacity: 0, x: -6 }} animate={{ opacity: 1, x: 0 }}
                className={`block p-3 border-b border-border hover:bg-muted/50 ${n.read ? "opacity-70" : ""}`}
                data-testid={`notif-item-${n.id}`}
              >
                <div className="flex items-start gap-2">
                  <div className={`h-2 w-2 rounded-full mt-1.5 ${n.read ? "bg-muted-foreground/40" : "bg-emerald-brand"}`} />
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm font-medium ${iconColor[n.kind] || ""}`}>{n.title}</div>
                    {n.body && <div className="text-xs text-muted-foreground mt-0.5">{n.body}</div>}
                    <div className="text-[10px] font-mono text-muted-foreground uppercase mt-1">{n.kind} · {(n.created_at || "").slice(11, 16)}</div>
                  </div>
                </div>
              </motion.a>
            ))}
          </AnimatePresence>
        </div>
      </PopoverContent>
    </Popover>
  );
}
